/**
 * Where the work is on its way in (AIOE 0.65.0): the agents' and plans' branches on the left, the integration branch
 * they land on, the default branch it lands on. Laid out by branchMapLayout.ts (copied from the workbench, whose Git
 * view draws the same map on a canvas); drawn here with plain cards and lines. A card picks that branch for the graph.
 */

import type { ProjectGitBranch } from '@aioe/shared';
import { BAND_HEAD, BAND_PAD } from '../flowStyle.ts';
import { BRANCH_CARD, layoutBranchMap } from './branchMapLayout.ts';

/** The most work branches the map shows; the rest are in the branch picker. */
const WORK_MAX = 8;

const COLUMN_TEXT = {
  work: { title: 'Being worked on', text: "Agents' own branches and plans' branches" },
  integration: { title: 'Approved, going in', text: 'Approved work collects here before it merges' },
  base: { title: 'Default branch', text: 'Where finished work lands' },
} as const;

const KIND_TEXT: Record<ProjectGitBranch['kind'], string> = { base: 'default branch', integration: 'integration', plan: "a plan's branch", agent: 'an agent', other: 'a branch' };

function cardWords(b: ProjectGitBranch): string {
  if (b.kind === 'agent' && b.agent) return `${b.agent.member} of ${b.agent.teamSlug}${b.agent.planSlug ? `, for ${b.agent.planSlug}` : ''}`;
  return KIND_TEXT[b.kind];
}

/** Whether a repository has anything for the map to say: an agent's, a plan's or an integration branch. */
export const hasBranchMap = (branches: ProjectGitBranch[]) => branches.some((b) => b.kind === 'agent' || b.kind === 'integration' || b.kind === 'plan');

export function BranchMap({ branches, selected, onPick }: { branches: ProjectGitBranch[]; selected?: string; onPick: (name: string) => void }) {
  const base = branches.find((b) => b.default);
  const integrations = branches.filter((b) => b.kind === 'integration');
  const integration = integrations.find((b) => base && b.name === `mojoup/integration/${base.name}`) ?? integrations[0];
  const allWork = branches.filter((b) => b.kind === 'agent' || b.kind === 'plan');
  const work = allWork.slice(0, WORK_MAX);
  const layout = layoutBranchMap({ base, integration, work });
  // The layout's origin is the first card's corner; bands reach BAND_PAD left of it and BAND_HEAD above.
  const dx = BAND_PAD;
  const dy = BAND_HEAD;
  const width = Math.max(...layout.bands.map((b) => b.rect.x + b.rect.width)) + dx;
  const height = Math.max(...layout.bands.map((b) => b.rect.y + b.rect.height)) + dy;
  const target = integration ? layout.cards.find((c) => c.column === 'integration') : layout.cards.find((c) => c.column === 'base');
  const baseCard = layout.cards.find((c) => c.column === 'base');
  const mid = (c: { x: number; y: number }, side: 'left' | 'right') => ({ x: c.x + dx + (side === 'right' ? BRANCH_CARD.width : 0), y: c.y + dy + BRANCH_CARD.height / 2 });
  const curve = (a: { x: number; y: number }, b: { x: number; y: number }) => `M ${a.x} ${a.y} C ${(a.x + b.x) / 2} ${a.y}, ${(a.x + b.x) / 2} ${b.y}, ${b.x} ${b.y}`;
  return (
    <div className="branch-map">
      <div className="branch-map__canvas" style={{ width, height }}>
        {layout.bands.map((b) => (
          <div key={b.column} className="branch-map__band" style={{ left: b.rect.x + dx, top: b.rect.y + dy, width: b.rect.width, height: b.rect.height }}>
            <span className="branch-map__band-title">
              {COLUMN_TEXT[b.column].title}
              {b.column === 'work' && allWork.length > work.length ? <span className="font-normal text-muted"> · {allWork.length - work.length} more in the picker</span> : null}
            </span>
            <span className="branch-map__band-text">{COLUMN_TEXT[b.column].text}</span>
          </div>
        ))}
        <svg className="branch-map__lines" width={width} height={height} aria-hidden="true">
          {target
            ? layout.cards
                .filter((c) => c.column === 'work')
                .map((c) => <path key={c.branch.name} d={curve(mid(c, 'right'), mid(target, 'left'))} fill="none" stroke="var(--separator)" strokeWidth={1.5} />)
            : null}
          {integration && baseCard && target ? <path d={curve(mid(target, 'right'), mid(baseCard, 'left'))} fill="none" stroke="var(--accent)" strokeOpacity={0.6} strokeWidth={1.5} /> : null}
        </svg>
        {layout.cards.map((c) => (
          <button
            key={c.branch.name}
            type="button"
            className="branch-map__card"
            data-selected={selected === c.branch.name || undefined}
            style={{ left: c.x + dx, top: c.y + dy, width: BRANCH_CARD.width, height: BRANCH_CARD.height }}
            onClick={() => onPick(c.branch.name)}
            aria-label={`Show the history of ${c.branch.name}`}
          >
            <span className="truncate font-mono text-sm" title={c.branch.name}>
              {c.branch.name}
            </span>
            <span className="truncate text-xs text-muted">{cardWords(c.branch)}</span>
            <span className="font-mono text-2xs text-muted tabular-nums">{c.branch.short}</span>
          </button>
        ))}
      </div>
    </div>
  );
}
